'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Lock, Eye, EyeOff, Mail, CheckCircle } from 'lucide-react';

interface LoginFormProps {
  onLogin: (password: string) => Promise<boolean>;
  authMethod?: 'password' | 'email';
}

export function LoginForm({ onLogin, authMethod = 'password' }: LoginFormProps) {
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await onLogin(password);
      
      if (!success) {
        setError('Invalid password. Please try again.');
        setPassword('');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMagicLinkSent(false);

    try {
      const response = await fetch('/api/auth/request-magic-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMagicLinkSent(true);
        setError('');
      } else {
        setError(data.error || 'Failed to send magic link. Please try again.');
      }
    } catch (error) {
      console.error('Magic link request error:', error);
      setError('Failed to send magic link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Render password authentication form
  if (authMethod === 'password') {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-surface to-primary-50 dark:from-background dark:via-surface dark:to-primary-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accessnow-orange text-white">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="text-2xl font-bold">
              <span className="text-accessnow-orange">Access</span>
              <span className="text-black dark:text-white">Now</span>
            </div>
          </div>
          
          <div>
            <CardTitle className="text-xl">ROI Calculator</CardTitle>
            <CardDescription className="text-muted-accessible">
              Please enter the access password to continue
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="pr-10"
                  required
                  aria-describedby={error ? "password-error" : undefined}
                  aria-invalid={!!error}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  )}
                </Button>
              </div>
            </div>
            
            {error && (
              <div 
                id="password-error"
                role="alert"
                className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 p-3 rounded-lg border border-red-200 dark:border-red-800"
              >
                <Lock className="h-4 w-4" aria-hidden="true" />
                {error}
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !password}
            >
              {isLoading ? 'Verifying...' : 'Access Calculator'}
            </Button>
          </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render email authentication form (magic link)
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-surface to-primary-50 dark:from-background dark:via-surface dark:to-primary-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accessnow-orange text-white">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="text-2xl font-bold">
              <span className="text-accessnow-orange">Access</span>
              <span className="text-black dark:text-white">Now</span>
            </div>
          </div>
          
          <div>
            <CardTitle className="text-xl">ROI Calculator</CardTitle>
            <CardDescription className="text-muted-accessible">
              {magicLinkSent 
                ? 'Check your email for the sign-in link' 
                : 'Enter your email to receive a sign-in link'}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          {magicLinkSent ? (
            <div className="space-y-4">
              <div 
                role="status"
                className="flex items-start gap-3 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/10 p-4 rounded-lg border border-green-200 dark:border-green-800"
              >
                <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="font-semibold mb-1">Magic link sent!</p>
                  <p className="text-green-700 dark:text-green-300">
                    You will receive a sign-in link shortly. 
                    Check your inbox and click the link to access the calculator.
                  </p>
                </div>
              </div>
              
              <Button 
                type="button"
                variant="outline"
                className="w-full" 
                onClick={() => {
                  setMagicLinkSent(false);
                  setEmail('');
                }}
              >
                Send Another Link
              </Button>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@company.com"
                    className="pl-10"
                    required
                    aria-describedby={error ? "email-error" : undefined}
                    aria-invalid={!!error}
                  />
                </div>
              </div>
              
              {error && (
                <div 
                  id="email-error"
                  role="alert"
                  className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 p-3 rounded-lg border border-red-200 dark:border-red-800"
                >
                  <Lock className="h-4 w-4" aria-hidden="true" />
                  {error}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || !email}
              >
                {isLoading ? 'Sending...' : 'Send Magic Link'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}